import { getSiteApi } from '@/api-calls/sites/get'
import { updateSiteImagesApi } from '@/api-calls/sites/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import {
  Button,
  Input,
  Label,
  Skeleton,
} from '@blogging/ui'
import { SectionHeading } from './section-heading'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { ImageIcon, Loader2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface ImageUploadProps {
  label: string
  description: string
  currentImage?: string | null
  onFileSelect: (file: File | null) => void
  selectedFile: File | null
  markedForRemoval: boolean
  onRemove: () => void
  accept?: string
}

function ImageUploadField({
  label,
  description,
  currentImage,
  onFileSelect,
  selectedFile,
  markedForRemoval,
  onRemove,
  accept = 'image/*',
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const previewUrl = markedForRemoval
    ? null
    : selectedFile
      ? URL.createObjectURL(selectedFile)
      : currentImage

  return (
    <div className="flex items-center gap-4">
      <div
        className="h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-dashed bg-muted/50 flex items-center justify-center hover:border-primary transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className="h-full w-full object-contain"
          />
        ) : (
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-0.5">
        <Label className="text-sm font-medium">{label}</Label>
        <p className="truncate text-xs text-muted-foreground">
          {markedForRemoval ? 'Will be removed on save' : description}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              onFileSelect(file)
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" />
        </Button>
        {(selectedFile || currentImage) && !markedForRemoval && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  )
}

export function SiteSettingsDesignPage() {
  const { siteId } = useParams()
  const queryClient = useQueryClient()
  const [logo, setLogo] = useState<File | null>(null)
  const [logoIcon, setLogoIcon] = useState<File | null>(null)
  const [logoFavicon, setLogoFavicon] = useState<File | null>(null)
  const [removeLogo, setRemoveLogo] = useState(false)
  const [removeLogoIcon, setRemoveLogoIcon] = useState(false)
  const [removeLogoFavicon, setRemoveLogoFavicon] = useState(false)

  const {
    data: site,
    isLoading,
    error,
  } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: { id: siteId as string },
  })

  const { mutate: updateImages, isPending: isUpdatingImages } = useMutation(
    updateSiteImagesApi,
    {
      resourceName: 'site',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getSiteApi.apiName, { id: siteId }],
        })
        toast.success('Site images updated successfully!')
        setLogo(null)
        setLogoIcon(null)
        setLogoFavicon(null)
        setRemoveLogo(false)
        setRemoveLogoIcon(false)
        setRemoveLogoFavicon(false)
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update site images')
      },
    },
  )

  const handleSaveImages = () => {
    if (
      !logo &&
      !logoIcon &&
      !logoFavicon &&
      !removeLogo &&
      !removeLogoIcon &&
      !removeLogoFavicon
    ) {
      toast.error('Please select at least one image to upload or remove')
      return
    }

    updateImages({
      id: siteId,
      logo,
      logoIcon,
      logoFavicon,
      removeLogo,
      removeLogoIcon,
      removeLogoFavicon,
    })
  }

  const hasImageChanges =
    logo ||
    logoIcon ||
    logoFavicon ||
    removeLogo ||
    removeLogoIcon ||
    removeLogoFavicon

  if (isLoading) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <div className="flex flex-col space-y-6">
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  if (error) {
    return (
      <ContainerWithSidebarAndNoTopbar>
        <div className="flex flex-col space-y-6">
          <div className="text-destructive">Failed to load site data</div>
        </div>
      </ContainerWithSidebarAndNoTopbar>
    )
  }

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col space-y-1">
          <h1 className="text-2xl font-bold">Site Design</h1>
          <p className="text-sm text-muted-foreground">
            Customize your site's appearance and branding.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-5 space-y-4">
          <SectionHeading>Brand Images</SectionHeading>

          <div className="divide-y">
            <div className="py-3 first:pt-0 last:pb-0">
              <ImageUploadField
                label="Logo"
                description="Main logo, PNG or SVG · at least 200x50px"
                currentImage={site?.logo}
                onFileSelect={(file) => {
                  setLogo(file)
                  if (file) setRemoveLogo(false)
                }}
                selectedFile={logo}
                markedForRemoval={removeLogo}
                onRemove={() => {
                  setLogo(null)
                  setRemoveLogo(true)
                }}
              />
            </div>

            <div className="py-3 first:pt-0 last:pb-0">
              <ImageUploadField
                label="Logo Icon"
                description="Square version, PNG or SVG · 512x512px"
                currentImage={site?.logoIcon}
                onFileSelect={(file) => {
                  setLogoIcon(file)
                  if (file) setRemoveLogoIcon(false)
                }}
                selectedFile={logoIcon}
                markedForRemoval={removeLogoIcon}
                onRemove={() => {
                  setLogoIcon(null)
                  setRemoveLogoIcon(true)
                }}
              />
            </div>

            <div className="py-3 first:pt-0 last:pb-0">
              <ImageUploadField
                label="Favicon"
                description="Browser tab icon, ICO or PNG · 32x32px"
                currentImage={site?.logoFavicon}
                onFileSelect={(file) => {
                  setLogoFavicon(file)
                  if (file) setRemoveLogoFavicon(false)
                }}
                selectedFile={logoFavicon}
                markedForRemoval={removeLogoFavicon}
                onRemove={() => {
                  setLogoFavicon(null)
                  setRemoveLogoFavicon(true)
                }}
                accept="image/x-icon,image/png,image/svg+xml"
              />
            </div>
          </div>

          <div className="flex flex-col items-start gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Upload new images to update your site's branding.
            </p>
            <Button
              onClick={handleSaveImages}
              disabled={isUpdatingImages || !hasImageChanges}
              className="w-full sm:w-auto"
            >
              {isUpdatingImages && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isUpdatingImages ? 'Saving...' : 'Save Images'}
            </Button>
          </div>
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
